import axios, { CancelToken } from 'axios'
import { isNotFoundError } from './ChannelProductAssociationHelper'

/**
 * Fila das consultas de produto no canal feitas pelos cards do detalhe do produto.
 *
 * Cada consulta atravessa a API do Muven, o agente e a API do marketplace, então
 * a tela não pode disparar tudo de uma vez nem repetir a mesma consulta a cada
 * visita. A fila limita quantas rodam juntas, junta pedidos iguais em andamento,
 * guarda o resultado por alguns minutos e corta quem demora demais.
 */

export class LookupCancelledError extends Error {
    constructor() {
        super('Consulta cancelada')
        Object.setPrototypeOf(this, LookupCancelledError.prototype)
    }
}

export class LookupTimeoutError extends Error {
    constructor(timeoutMs:number) {
        super(`O canal não respondeu em ${Math.round(timeoutMs / 1000)}s`)
        Object.setPrototypeOf(this, LookupTimeoutError.prototype)
    }
}

export function isLookupCancelled(err:any):boolean {
    return err instanceof LookupCancelledError
}

export function isLookupTimeout(err:any):boolean {
    return err instanceof LookupTimeoutError
}

export function channelLookupKey(idStore:any, idChannel:any, idExternalChannel:any):string {
    return `${idStore}|${idChannel}|${idExternalChannel}`
}

interface CancelSource {
    token:CancelToken
    cancel:(message?:string) => void
}

export interface ChannelLookupQueueOptions {
    ttlMs:number
    concurrency:number
    timeoutMs:number
    // Erros que também valem guardar (ex.: "não encontrado" é uma resposta, não uma falha)
    shouldCacheError?:(err:any) => boolean
    now?:() => number
    createCancelSource?:() => CancelSource
}

type Task<T> = (cancelToken:CancelToken) => Promise<T>

interface CacheEntry {
    expiresAt:number
    ok:boolean
    value:any
}

interface Job {
    key:string
    task:Task<any>
    resolve:(value:any) => void
    reject:(err:any) => void
    settled:boolean
    source?:CancelSource
    timer?:any
}

export class ChannelLookupQueue {
    private readonly options:ChannelLookupQueueOptions
    private readonly cache = new Map<string, CacheEntry>()
    private readonly inflight = new Map<string, Promise<any>>()
    private queue:Job[] = []
    private running:Job[] = []

    constructor(options:ChannelLookupQueueOptions) {
        this.options = options
    }

    run<T>(key:string, task:Task<T>, {force = false}:{force?:boolean} = {}):Promise<T> {
        if (force) {
            this.cache.delete(key)
        } else {
            const cached = this.readCache(key)

            if (cached) {
                return cached.ok ? Promise.resolve(cached.value) : Promise.reject(cached.value)
            }
        }

        // Mesmo pedido em andamento: reaproveita, inclusive no "tentar de novo"
        const pending = this.inflight.get(key)

        if (pending) {
            return pending
        }

        const promise = new Promise<T>((resolve, reject) => {
            this.queue.push({key, task, resolve, reject, settled: false})
        })

        this.inflight.set(key, promise)
        this.pump()

        return promise
    }

    invalidate(key:string):void {
        this.cache.delete(key)
    }

    // Cancela o que está na fila e o que está em voo. O cache fica para a próxima visita.
    cancelAll():void {
        const jobs = [...this.queue, ...this.running]
        this.queue = []

        jobs.forEach((job) => {
            if (job.source) {
                job.source.cancel()
            }

            this.finish(job, false, new LookupCancelledError())
        })
    }

    private readCache(key:string):CacheEntry | null {
        const entry = this.cache.get(key)

        if (!entry) {
            return null
        }

        if (entry.expiresAt <= this.now()) {
            this.cache.delete(key)
            return null
        }

        return entry
    }

    private writeCache(key:string, ok:boolean, value:any):void {
        const now = this.now()

        this.cache.forEach((entry, k) => {
            if (entry.expiresAt <= now) {
                this.cache.delete(k)
            }
        })

        this.cache.set(key, {expiresAt: now + this.options.ttlMs, ok, value})
    }

    private pump():void {
        while (this.running.length < this.options.concurrency && this.queue.length) {
            this.start(this.queue.shift() as Job)
        }
    }

    private start(job:Job):void {
        this.running.push(job)
        job.source = this.createCancelSource()

        job.timer = setTimeout(() => {
            if (job.source) {
                job.source.cancel()
            }

            this.finish(job, false, new LookupTimeoutError(this.options.timeoutMs))
        }, this.options.timeoutMs)

        let result:Promise<any>

        try {
            result = Promise.resolve(job.task(job.source.token))
        } catch (err) {
            result = Promise.reject(err)
        }

        result.then(
            (value) => {
                if (!job.settled) {
                    this.writeCache(job.key, true, value)
                }

                this.finish(job, true, value)
            },
            (err) => {
                if (!job.settled && this.options.shouldCacheError && this.options.shouldCacheError(err)) {
                    this.writeCache(job.key, false, err)
                }

                this.finish(job, false, err)
            },
        )
    }

    private finish(job:Job, ok:boolean, value:any):void {
        if (job.settled) {
            return
        }

        job.settled = true
        clearTimeout(job.timer)

        if (this.inflight.get(job.key)) {
            this.inflight.delete(job.key)
        }

        const index = this.running.indexOf(job)

        if (index > -1) {
            this.running.splice(index, 1)
        }

        if (ok) {
            job.resolve(value)
        } else {
            job.reject(value)
        }

        this.pump()
    }

    private now():number {
        return this.options.now ? this.options.now() : Date.now()
    }

    private createCancelSource():CancelSource {
        return this.options.createCancelSource ? this.options.createCancelSource() : axios.CancelToken.source()
    }
}

export const CHANNEL_LOOKUP_TIMEOUT_MS = 15000

export const channelLookupQueue = new ChannelLookupQueue({
    ttlMs: 3 * 60 * 1000,
    concurrency: 2,
    timeoutMs: CHANNEL_LOOKUP_TIMEOUT_MS,
    shouldCacheError: isNotFoundError,
})
